import { useMemo, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    type ColumnDef,
    type ColumnFiltersState,
    type SortingState,
} from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/kit';
import { platformApi } from '../../../api/endpoints/platform';
import { useApplications } from '../../../hooks/useApplications';
import { Gate } from '../../../components/permissions/Gate';
import { Badge } from '../../../components/ui/badge';
import { CreateTenantWizard } from './CreateTenantWizard';
import { EditTenantModal } from './EditTenantModal';
import { TenantDetail } from './TenantDetail';
import { TenantRowActions } from './TenantRowActions';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../components/ui/table';
import { DataTableToolbar } from '../../../components/shared/DataTableToolbar';
import { DataTablePagination } from '../../../components/shared/DataTablePagination';
import { SortableHeader } from '../../../components/shared/SortableHeader';
import { ContentLoading } from '../../../components/shared/ContentLoading';
import { globalFilterFn } from '../../../lib/dataTable';
import { SELECT_CLASS } from '../../../lib/selectClass';
import { formatDateMx } from '../../../lib/dateHelpers';
import type {
    PlatformTenantListItemLocal,
    PlatformTenantStatusLocal,
} from '../../../api/types/PlatformLocal';

// El backend devuelve el status del tenant en minúsculas (openapi: 'provisioning' | 'active' |
// 'suspended' | 'pending-infra'). Cada uno → label legible + variante de badge (mismo criterio visual
// que las otras tablas del repo: Activo=default, Suspendido=destructive, en provisión=outline).
//
// `pending-infra` (DEC-RBAC-058) es un estado de ADVERTENCIA: el tenant existe pero no es operativo
// hasta que infra cree las tablas. DEC-021: el <Badge> ahora tiene variante `warning` nativa con el
// token semántico → la usamos directo (antes era `outline` + override de color ámbar por className).
const STATUS_META: Record<
    PlatformTenantStatusLocal,
    {
        label: string;
        variant: 'default' | 'secondary' | 'outline' | 'destructive' | 'warning';
        className?: string;
    }
> = {
    provisioning: { label: 'En provisión', variant: 'outline' },
    active: { label: 'Activo', variant: 'default' },
    suspended: { label: 'Suspendido', variant: 'destructive' },
    'pending-infra': {
        label: 'Pendiente de infra',
        variant: 'warning',
    },
};

const STATUS_OPTIONS = Object.keys(STATUS_META) as PlatformTenantStatusLocal[];

const baseColumns: ColumnDef<PlatformTenantListItemLocal>[] = [
    {
        accessorKey: 'displayName',
        header: ({ column }) => <SortableHeader column={column} title="Nombre" />,
    },
    {
        accessorKey: 'status',
        header: ({ column }) => <SortableHeader column={column} title="Estado" />,
        filterFn: 'equals',
        cell: (info) => {
            const status = info.getValue() as PlatformTenantStatusLocal | undefined;
            const meta = status ? STATUS_META[status] : undefined;
            return (
                <Badge variant={meta?.variant ?? 'secondary'} className={meta?.className}>
                    {meta?.label ?? '—'}
                </Badge>
            );
        },
    },
    {
        accessorKey: 'createdAt',
        header: ({ column }) => <SortableHeader column={column} title="Creado" />,
        enableGlobalFilter: false,
        cell: (info) => {
            const iso = info.getValue() as string | undefined;
            return iso ? formatDateMx(iso) : '—';
        },
    },
];

/**
 * Listado de tenants del módulo Platform (vista multi-tenant del backoffice). Misma composición de
 * tabla que UsersTable/RetailersTable (useReactTable + DataTableToolbar/Pagination + SortableHeader).
 * El list NO es paginado por cursor (platformApi.listTenants devuelve { items }), así que usa
 * useQuery simple; la paginación es client-side sobre lo cargado.
 */
export function TenantsTable() {
    const [createOpen, setCreateOpen] = useState(false);
    const [viewTenant, setViewTenant] = useState<PlatformTenantListItemLocal | null>(null);
    const [editTenant, setEditTenant] = useState<PlatformTenantListItemLocal | null>(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    // Lo más nuevo arriba: el tenant recién creado abre la tabla. El alfabético sigue a un clic.
    const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);

    const { data, isPending } = useQuery({
        queryKey: ['platform', 'tenants'],
        queryFn: () => platformApi.listTenants(),
    });

    // Mapa applicationId → displayName de la app, para resolver la columna "Aplicación" por NOMBRE
    // (regla anti-UUID: nunca el applicationId crudo en UI). Si la lista de apps aún no cargó, el map
    // queda vacío y la celda cae a '—'.
    const { data: applications } = useApplications();
    const appNameById = useMemo(() => {
        const map = new Map<string, string>();
        for (const app of applications ?? []) map.set(app.applicationId, app.displayName);
        return map;
    }, [applications]);

    const columns = useMemo<ColumnDef<PlatformTenantListItemLocal>[]>(
        () => [
            baseColumns[0],
            {
                accessorKey: 'applicationId',
                header: ({ column }) => <SortableHeader column={column} title="Aplicación" />,
                cell: (info) => {
                    const appId = info.getValue() as string | undefined;
                    const name = appId ? appNameById.get(appId) : undefined;
                    return name ?? '—';
                },
            },
            ...baseColumns.slice(1),
            {
                id: 'actions',
                header: 'Acciones',
                enableSorting: false,
                enableGlobalFilter: false,
                cell: (info) => (
                    <TenantRowActions
                        tenant={info.row.original}
                        onView={setViewTenant}
                        onEdit={setEditTenant}
                    />
                ),
            },
        ],
        [appNameById],
    );

    const columnFilters: ColumnFiltersState = useMemo(
        () => (statusFilter ? [{ id: 'status', value: statusFilter }] : []),
        [statusFilter],
    );

    // useMemo OBLIGATORIO: useReactTable compara `data` por referencia; sin memo el array nuevo por
    // render dispara re-render loop (footgun documentado en UsersTable).
    const rows = useMemo(() => data?.items ?? [], [data]);
    const table = useReactTable({
        data: rows,
        columns,
        state: { globalFilter, columnFilters, sorting },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        globalFilterFn,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 25 } },
    });

    if (isPending) return <ContentLoading />;

    return (
        <div className="space-y-3">
            <div className="flex justify-end">
                <Gate permission="platform.tenant.create" scope="PLATFORM">
                    <Button
                        onClick={() => {
                            setCreateOpen(true);
                        }}
                    >
                        Crear tenant
                    </Button>
                </Gate>
            </div>

            <CreateTenantWizard open={createOpen} onOpenChange={setCreateOpen} />

            {viewTenant && (
                <TenantDetail
                    tenantId={viewTenant.tenantId}
                    open
                    onOpenChange={(o) => {
                        if (!o) setViewTenant(null);
                    }}
                />
            )}

            {editTenant && (
                <EditTenantModal
                    tenant={editTenant}
                    open
                    onOpenChange={(o) => {
                        if (!o) setEditTenant(null);
                    }}
                />
            )}

            <DataTableToolbar value={globalFilter} onChange={setGlobalFilter}>
                <select
                    aria-label="Estado"
                    className={`${SELECT_CLASS} w-auto`}
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                    }}
                >
                    <option value="">Todos</option>
                    {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                            {STATUS_META[s].label}
                        </option>
                    ))}
                </select>
            </DataTableToolbar>

            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                            {hg.headers.map((h) => (
                                <TableHead key={h.id} className="text-center">
                                    {flexRender(h.column.columnDef.header, h.getContext())}
                                </TableHead>
                            ))}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((r) => (
                        <TableRow key={r.id}>
                            {r.getVisibleCells().map((c) => (
                                <TableCell key={c.id} className="text-center align-middle">
                                    {flexRender(c.column.columnDef.cell, c.getContext())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <DataTablePagination table={table} />
        </div>
    );
}
